import React from 'react';
import LobbyPlayerName from '../LobbyPlayerName';
import LobbyDesktopPlayerBadges from './LobbyDesktopPlayerBadges';
import LobbyFactionTiles from './LobbyFactionTiles';
import {getLobbyFactionDisplay} from './lobbyFactionDisplay';
import PageButton from '../../elements/PageButton/PageButton';

/**
 * Плитки четверки соперника рейтингового лобби. Во время банов одиночное
 * нажатие — голос за бан (с задержкой ради двойного, см. onBanVoteClick),
 * двойное — карточка фракции; на плитке — чипы голосовавших, свой голос —
 * красным контуром. После банов — только итог банов (черная метка,
 * обесцвечивание); нажатие — карточка фракции.
 *
 * Какую фракцию соперник выбрал, до старта матча не показывается: открыты
 * лишь голоса банов, а пик — тайна, иначе последний выбирающий подбирал бы
 * фракцию против уже выбранных. Готовность видна только меткой «Готов» (в
 * окне выбора — выбрал и подтвердил), иначе — «Выбирает фракцию».
 */
const buildOpponentRankedTiles = ({player, banView, onBanVoteClick, onOpenFactionCard}) => (
  (player.availableFactionIds || []).map((factionId) => {
    const display = getLobbyFactionDisplay(factionId, player.selectedSkins?.[factionId]);
    const openCard = () => onOpenFactionCard(player, factionId);
    if (banView.isPhase) {
      const isOwnVote = banView.ownVotes[player.id] === factionId;
      const canVote = banView.isVoteAllowed;
      return {
        factionId,
        display,
        voters: banView.getFactionVoters(player.id, factionId),
        banMark: isOwnVote ? 'vote' : null,
        banTitle: canVote ? 'Ваш голос за бан — нажмите, чтобы снять' : 'Ваш голос за бан',
        isInteractive: canVote,
        title: canVote
          ? `${display.name}: нажмите — голос за бан, «i» или двойное нажатие — карточка фракции`
          : `${display.name}: «i» или двойное нажатие — карточка фракции`,
        onClick: canVote ? () => onBanVoteClick(player.id, factionId) : undefined,
        onDoubleClick: openCard,
        // Посмотреть чужую фракцию, не голосуя: одиночное нажатие в фазе — голос
        onInfo: openCard,
      };
    }
    const isBanned = player.bannedFactionId === factionId;
    return {
      factionId,
      display,
      voters: [],
      banMark: isBanned ? 'final' : null,
      banTitle: 'Фракция забанена',
      isInteractive: true,
      title: display.name,
      onClick: openCard,
      onInfo: openCard,
    };
  })
);

/**
 * Карточка соперника десктопного лобби. В обычном лобби — «рубашка»: выбор
 * соперника до старта матча тайна, поэтому видно только, выбирает он еще
 * фракцию или уже выбрал (метки «Выбирает фракцию» и «Готов»); ник и бейджи. В рейтинговом — шапка и плитки его
 * четверки фракций (их видно всем — по ним банят). Раскладка — по форме
 * карточки (container queries, см. LobbyDesktop.css).
 *
 * @param {Object} props
 * @param {Object} props.player
 * @param {Object} props.lobby
 * @param {boolean} props.isKickVisible - создатель может исключить игрока
 * @param {Function} props.onKick
 * @param {Object} props.banView
 * @param {Object} props.selectView - окно выбора рейтингового лобби и готовность из LobbyPage
 * @param {Function} props.onBanVoteClick
 * @param {Function} props.onOpenFactionCard
 * @param {string} props.hiddenFactionsText - заглушка, пока фракции не выданы
 */
const LobbyOpponentCard = ({
  player, lobby, isKickVisible, onKick, banView, selectView, onBanVoteClick, onOpenFactionCard, hiddenFactionsText,
}) => {
  const isReady = selectView.checkIfPlayerReady(player);
  const badges = (
    <LobbyDesktopPlayerBadges
      player={player}
      isOwn={false}
      isLobbyCreator={player.id === lobby.creatorId}
      banStatus={banView.getPlayerStatus(player)}
      isReady={isReady}
      isChoosingShown={!lobby.isRanked || selectView.isWindow}
    />
  );

  const kickButton = isKickVisible && (
    <PageButton
      className="lobby-kick-button lobby-opponent-card_kick"
      onClick={() => onKick(player.id)}
      title="Исключить игрока"
      pageId="LobbyPage"
      buttonId="kick_player"
    >
      <div className={'lobby-kick-button_img'}/>
    </PageButton>
  );

  if (lobby.isRanked) {
    const hasFactions = (player.availableFactionIds || []).length > 0;
    return (
      <article className="lobby-opponent-card lobby-opponent-card_ranked" data-player-id={player.id}>
        <header className="lobby-desktop-card-header lobby-opponent-card_header">
          <div className="lobby-desktop-card-header_player">
            <LobbyPlayerName player={player}/>
            {badges}
          </div>
        </header>
        {hasFactions ? (
          <LobbyFactionTiles
            tiles={buildOpponentRankedTiles({player, banView, onBanVoteClick, onOpenFactionCard})}
            className="lobby-opponent-card_tiles"
          />
        ) : (
          <div className="lobby-desktop-card-placeholder">{hiddenFactionsText}</div>
        )}
        {kickButton}
      </article>
    );
  }

  return (
    <article
      className={`lobby-opponent-card lobby-opponent-card_regular ${isReady ? 'lobby-opponent-card_ready' : ''}`}
      data-player-id={player.id}
    >
      <div className="lobby-opponent-card_art-box lobby-opponent-card_empty-art">
        <img className="lobby-opponent-card_empty-bust" src="/img/other/strategikon_bust.png" alt=""/>
      </div>
      <div className="lobby-opponent-card_info">
        <LobbyPlayerName player={player}/>
        {badges}
      </div>
      {kickButton}
    </article>
  );
};

export default LobbyOpponentCard;
